// apps/client/src/state/slices/mail.ts (M1-T17) — mail + news; every key starts with "mail" (§8.7 key rule)
import { getEntry, t, type StringKey } from '@whiskerton/content';
import type { ItemId, ItemView, MailHeader, NewsItem, ResidentId } from '@whiskerton/shared';
import type { CoreCommands } from '../../game/commands/core';
import type { ActResult } from '../../net/actions';
import type { SliceCreator } from '../types';

export interface MailSlice {
  mailUnread: number;                                                          // unread letters in `you.mail` (the HUD Mail badge)
  mailNews: NewsItem[]; mailNewsMore: boolean;
  mailNewsHead: number; mailNewsSeen: number;                                  // newest news id in town / newest the player has seen
  mailSetUnread(n: number): void;
  mailSetNews(items: NewsItem[], more: boolean, append: boolean): void; mailPostNews(item: NewsItem): void;
  mailSetNewsHead(id: number): void; mailMarkNewsSeen(id: number): void;
}
// eslint-disable-next-line @typescript-eslint/no-empty-object-type -- declaration merging into GameStore
declare module '../types' { interface GameStore extends MailSlice {} }

const SEEN_KEY = 'wt:newsSeen';
const readSeen = (): number => { try { return Number(localStorage.getItem(SEEN_KEY) ?? 0) || 0; } catch { return 0; } };
export const slice: SliceCreator<MailSlice> = (set, get) => ({
  mailUnread: 0, mailNews: [], mailNewsMore: false, mailNewsHead: 0, mailNewsSeen: readSeen(),
  mailSetUnread: (n) => { if (get().mailUnread !== n) set({ mailUnread: n }); },
  mailSetNews: (items, more, append) => set({ mailNews: append ? [...get().mailNews, ...items] : items, mailNewsMore: more }),
  mailPostNews: (item) => set({ mailNews: [item, ...get().mailNews.filter((n) => n.id !== item.id)], mailNewsHead: Math.max(get().mailNewsHead, item.id) }),
  mailSetNewsHead: (id) => set({ mailNewsHead: id }),
  mailMarkNewsSeen: (id) => {
    try { localStorage.setItem(SEEN_KEY, String(id)); } catch { /* storage may be blocked */ }
    set({ mailNewsSeen: id });
  },
});

export const unreadMail = (headers: readonly MailHeader[]): number => headers.filter((m) => !m.read).length;
export const newsUnread = (newsHead: number, seen: number): boolean => newsHead > seen;
export const renderNews = (item: NewsItem): string => t(`news.${item.kind}` as StringKey, { ...item.params, name: item.actor.name });

export type Act = CoreCommands['act'];
/** Sends a letter with the first stationery in your pockets, and the first wrapping paper when a gift is wrapped. */
export async function sendLetter(act: Act, pockets: readonly ItemView[], a: { to: ResidentId; body: string; gift: ItemId | null; wrap: boolean }):
  Promise<ActResult<'mail.send'> | { ok: false; code: 'no_stationery' }> {
  const of = (kind: string) => pockets.find((i) => i.at.loc === 'pockets' && getEntry(i.catalogId)?.kind === kind)?.id ?? null;
  const stationery = of('stationery');
  if (!stationery) return { ok: false, code: 'no_stationery' };
  return act('mail.send', { to: a.to, body: a.body, stationery, gift: a.gift, wrapping: a.gift && a.wrap ? of('wrapping') : null });
}
